import { Flag } from '../shared/Flag';
import { useNavigate, useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Text, makeStyles, tokens, mergeClasses,
  Popover, PopoverTrigger, PopoverSurface,
  Badge, Divider, Spinner, Dropdown, Option,
  useToastController, Toast, ToastTitle, ToastBody,
} from '@fluentui/react-components';
import {
  SignOut24Regular, Navigation24Regular,
  Alert24Regular, AlertBadge24Regular,
  WeatherSunny24Regular, WeatherMoon24Regular,
  Mail24Regular, Phone24Regular,
  ChevronDown24Regular,
  LocalLanguage24Regular,
  QuestionCircle24Regular,
  QuestionCircle20Regular,
} from '@fluentui/react-icons';
import { SystemGuideModal } from '../guide/SystemGuideModal';
import { TelegramLinkSection } from './TelegramLinkSection';
import { useProductTour } from '../../tour/ProductTour';
import { useThemeStore } from '../../store/theme.store';
import { useAuthStore } from '../../store/auth.store';
import { useTenantStore } from '../../store/tenant.store';
import { useProjectStore } from '../../store/project.store';
import { useRanchoStore } from '../../store/rancho.store';
import { useLocaleStore, type Locale } from '../../store/locale.store';
import { fetchProjects } from '../../api/terrain.api';
import { fetchRanchos } from '../../api/rancho.api';
import { fetchLotes } from '../../api/lotes.api';
import { isCurrentTenant } from './tenantUtils';
import { usePermission } from '../../hooks/usePermission';
import { useT, LOCALE_OPTIONS } from '../../i18n';
import { useNotifications } from '../../hooks/useNotifications';
import { getUserTenants, type UserTenant } from '../../api/users.api';
import { impersonateTenant } from '../../api/tenants.api';
import { tenantHostUrl } from '../../utils/host-tenant';
import { apiErrorMessage } from '../../api/apiError';
import { updateMyPreferences, updateMyDayCloseTime, getMe } from '../../api/settings.api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Devuelve las iniciales (máx 2 letras) a partir del nombre completo */
function getInitials(name?: string, fallback?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (fallback) return fallback.slice(0, 2).toUpperCase();
  return '?';
}

/** Color determinista por nombre (evita que todos tengan el mismo tono) */
const AVATAR_COLORS = [
  '#2e7d32', '#1565c0', '#6a1b9a', '#c62828',
  '#ef6c00', '#00695c', '#283593', '#558b2f',
];
function avatarColor(seed?: string): string {
  if (!seed) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + (hash << 5) - hash;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatRelativeTime(iso: string, t: ReturnType<typeof useT>): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diffMs / 60_000);
  if (mins < 1)  return t.topBar.timeJustNow;
  if (mins < 60) return t.topBar.timeMinutesAgo.replace('{n}', String(mins));
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return t.topBar.timeHoursAgo.replace('{n}', String(hrs));
  return t.topBar.timeDaysAgo.replace('{n}', String(Math.floor(hrs / 24)));
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  bar: {
    height: '52px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  menuBtn: {
    display: 'none',
    '@media (max-width: 768px)': { display: 'flex' },
  },
  ranchoName: {
    '@media (max-width: 480px)': { display: 'none' },
  },

  // ── Avatar circle ──────────────────────────────────────────────────────
  avatarBtn: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    padding: '0',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      border: `2px solid ${tokens.colorBrandStroke1}`,
    },
  },
  avatarInner: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#fff',
    userSelect: 'none',
    lineHeight: '1',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },

  // ── Profile popover ────────────────────────────────────────────────────
  profilePanel: {
    minWidth: '260px',
    maxWidth: '320px',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px 16px 12px',
  },
  profileAvatar: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
    flexShrink: 0,
    overflow: 'hidden',
  },
  profileAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  profileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflow: 'hidden',
  },
  profileName: {
    fontSize: '15px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  profileRole: {
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
    fontWeight: '500',
  },
  profileFields: {
    padding: '8px 16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  profileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  profileRowIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
  },
  telegramLinked: {
    color: '#2AABEE',   // Telegram brand blue
  },
  telegramUnlinked: {
    color: tokens.colorNeutralForeground4,
  },
  profileFooter: {
    padding: '8px 12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  // ── Bell ──────────────────────────────────────────────────────────────
  bellWrap: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  bellBadge: {
    position: 'absolute' as const,
    top: '-4px',
    right: '-4px',
    pointerEvents: 'none',
  },
  panel: {
    width: '340px',
    maxWidth: '90vw',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '420px',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  panelList: {
    overflowY: 'auto',
    flex: 1,
  },
  notifItem: {
    padding: '10px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  notifItemUnread: {
    background: tokens.colorBrandBackground2,
  },
  notifTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  notifBody: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  notifTime: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    marginTop: '2px',
  },
  panelEmpty: {
    padding: '32px 16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },

  // ── Tenant selector ────────────────────────────────────────────────────
  tenantSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  tenantSelectorLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  tenantDropdown: {
    minWidth: '200px',
    maxWidth: '300px',
    maxHeight: '300px',
    overflowY: 'auto' as const,
  },
  tenantDropdownItem: {
    padding: '8px 12px',
    cursor: 'pointer',
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
    },
  },
  tenantDropdownItemSelected: {
    background: tokens.colorBrandBackground2,
    fontWeight: 600,
  },
  tenantDropdownLoading: {
    padding: '16px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },

  // ── Language switcher ──────────────────────────────────────────────────
  langPanel: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '160px',
  },
  langOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    fontSize: '13px',
    border: 'none',
    background: 'transparent',
    textAlign: 'left' as const,
    color: tokens.colorNeutralForeground1,
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
    },
  },
  langOptionActive: {
    background: tokens.colorBrandBackground2,
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
  },

  // ── Version badge (Fase 2) ────────────────────────────────────────────
  versionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    padding: '4px 8px',
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    textDecoration: 'none',
    ':hover': {
      color: tokens.colorNeutralForeground2,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
  },
});

// ---------------------------------------------------------------------------
// Avatar — pequeño (TopBar)
// ---------------------------------------------------------------------------

function AvatarSmall({ name, photoUrl }: { name?: string; photoUrl?: string }) {
  const s = useStyles();
  const initials = getInitials(name, name);
  const bg = avatarColor(name);
  return (
    <span className={s.avatarInner} style={{ backgroundColor: bg }}>
      {photoUrl
        ? <img src={photoUrl} alt={name} className={s.avatarImg} />
        : initials}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Avatar — grande (perfil popover)
// ---------------------------------------------------------------------------

function AvatarLarge({ name, photoUrl }: { name?: string; photoUrl?: string }) {
  const s = useStyles();
  const initials = getInitials(name, name);
  const bg = avatarColor(name);
  return (
    <div className={s.profileAvatar} style={{ backgroundColor: bg }}>
      {photoUrl
        ? <img src={photoUrl} alt={name} className={s.profileAvatarImg} />
        : initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selector de idioma — visible para TODOS los roles (incl. INVESTOR), no
// depende de que "Ajustes" aparezca en el Sidebar. Persiste el cambio vía
// PUT /users/me/preferences para que el próximo login ya no lo pierda.
// ---------------------------------------------------------------------------

function LanguageSwitcher() {
  const s = useStyles();
  const t = useT();
  const { locale, setLocale } = useLocaleStore();
  const { mode } = useThemeStore();
  const [open, setOpen] = useState(false);

  const handleSelect = (value: Locale) => {
    setLocale(value);
    setOpen(false);
    void updateMyPreferences({ language: value, theme: mode }).catch(() => { /* best-effort */ });
  };

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          icon={<LocalLanguage24Regular />}
          appearance="subtle"
          size="small"
          aria-label={t.nav.changeLanguage}
          title={t.nav.changeLanguage}
        />
      </PopoverTrigger>
      <PopoverSurface>
        <div className={s.langPanel}>
          {LOCALE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={mergeClasses(s.langOption, locale === opt.value ? s.langOptionActive : undefined)}
              onClick={() => handleSelect(opt.value as Locale)}
              aria-pressed={locale === opt.value}
            >
              <Flag iso={opt.flagIso} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </PopoverSurface>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// TopBar
// ---------------------------------------------------------------------------

interface TopBarProps {
  onMenuClick?: () => void;
}

/**
 * Cierre de sesión: limpia el estado de auth, la caché de React Query (para no
 * mostrar datos del usuario anterior) y navega a /login. Extraído para poder
 * probarlo sin montar todo el TopBar.
 */
export function performLogout(deps: { logout: () => void; clearCache: () => void; navigate: (to: string) => void; tenantId?: string | null }) {
  // La demo tiene su propia pantalla de entrada por rol (/demo/login).
  const loginPath = deps.tenantId === 'demo' ? '/demo/login' : '/login';
  deps.logout();
  deps.clearCache();
  deps.navigate(loginPath);
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const s = useStyles();
  const t = useT();
  const { user, logout, impersonate } = useAuthStore();
  const { dispatchToast } = useToastController('agrovid-toaster');
  const { displayName: tenantDisplayName, logoUrl: tenantLogoUrl, tenantId: currentTenantId, setTenant } = useTenantStore();
  const navigate = useNavigate();
  const { notifications, unreadCount, lastSeenAt, markAllRead } = useNotifications();
  const { mode, toggle } = useThemeStore();
  const { start: startTour } = useProductTour();

  // F1 — selector global de proyecto/temporada. El resto de páginas filtra por
  // `activeProjectId`, así el usuario deja de teclear lote+ciclo en cada una.
  const { activeProjectId, setActiveProject } = useProjectStore();
  const canReadProjects = usePermission('projects:read');
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    enabled: canReadProjects,
    staleTime: 5 * 60 * 1000,
  });

  // Fase 1 rancho selector (2026-09-27) — distinto de Proyecto (que es una
  // temporada DENTRO de un rancho, no un selector de ubicación física).
  // Antes no existía ningún filtro por rancho en el dashboard: páginas como
  // FieldPage mostraban lotes de todos los ranchos del tenant mezclados.
  const { activeRanchoId, setActiveRancho } = useRanchoStore();
  const canReadRanchos = usePermission('ranchos:read');
  const { data: allRanchos = [], isSuccess: ranchosLoaded } = useQuery({
    queryKey: ['ranchos'],
    queryFn: fetchRanchos,
    enabled: canReadRanchos,
    staleTime: 5 * 60 * 1000,
  });
  // Solo se ofrecen los ranchos a los que el usuario tiene acceso —
  // `allowedRanchos` ausente/null significa acceso total (sin restricción),
  // mismo criterio que el resto del proyecto (ver `filterByAllowedLotes`).
  const visibleRanchos = user?.allowedRanchos?.length
    ? allRanchos.filter(r => user.allowedRanchos!.includes(r.ranchoId))
    : allRanchos;

  // El rancho activo se persiste en localStorage: si el rancho se borró, se
  // desactivó o es de otra empresa, el id quedaba huérfano y "Nuevo lote"
  // fallaba con "Rancho no encontrado". Se limpia en cuanto se carga la lista.
  useEffect(() => {
    if (!canReadRanchos || !activeRanchoId || !ranchosLoaded) return;
    if (!visibleRanchos.some(r => r.ranchoId === activeRanchoId)) setActiveRancho(null);
  }, [canReadRanchos, ranchosLoaded, activeRanchoId, visibleRanchos, setActiveRancho]);

  // Fase 2: Fetch backend version para mostrar en TopBar
  const { data: versionData } = useQuery({
    queryKey: ['system-version'],
    queryFn: async () => {
      const res = await fetch('/api/version');
      if (!res.ok) throw new Error('Failed to fetch version');
      return res.json();
    },
    staleTime: 10 * 60 * 1000,  // cache 10 min
  });
  const backendVersion = versionData?.backend || '';

  const { tenantId: routeTenantId } = useParams();

  // System Guide modal state
  const [guideOpen, setGuideOpen] = useState(false);

  // Cierre de día proactivo por Telegram (dayCloseTime) — self-service
  const { data: meData } = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60 * 1000 });
  // Rancho(s)/Lote(s) asignados al usuario (vacío/null = sin restricción = "Todos").
  // `me` es la fuente fresca (un ADMIN pudo cambiar el alcance tras el login); la sesión es el respaldo.
  const assignedRanchoIds = meData?.allowedRanchos ?? user?.allowedRanchos ?? [];
  const assignedLoteIds = meData?.allowedLotes ?? [];
  const { data: allLotes = [] } = useQuery({
    queryKey: ['lotes', null],
    queryFn: () => fetchLotes(null),
    enabled: assignedLoteIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const assignedRanchoNames = assignedRanchoIds.map(id => allRanchos.find(r => r.ranchoId === id)?.name ?? id);
  const assignedLoteNames = assignedLoteIds.map(id => allLotes.find(l => l.loteId === id)?.name ?? id);
  const [dayCloseTime, setDayCloseTime] = useState('');
  useEffect(() => {
    setDayCloseTime(meData?.dayCloseTime ?? '');
  }, [meData?.dayCloseTime]);

  const handleDayCloseTimeChange = (value: string) => {
    setDayCloseTime(value);
    if (!value) return; // el input vacío no dispara guardado — se usa el botón "Desactivar"
    void updateMyDayCloseTime(value).catch(() => { /* best-effort */ });
  };

  const handleDayCloseTimeDisable = () => {
    setDayCloseTime('');
    void updateMyDayCloseTime(null).catch(() => { /* best-effort */ });
  };

  // Tenant selector state
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);

  // Lista de empresas del usuario. Es una query compartida (['user-tenants']) para que
  // SuperAdminPage la invalide al activar/desactivar/crear/eliminar una empresa y el
  // selector se actualice sin recargar la página.
  const { data: tenantsData, isFetching: loadingTenants } = useQuery({
    queryKey: ['user-tenants', routeTenantId],
    queryFn: async () => {
      try {
        return (await getUserTenants()).tenants as UserTenant[];
      } catch (err: any) {
        // Degradación silenciosa: sin lista de tenants simplemente no se ofrece el selector.
        if (err?.response?.status !== 404) console.warn('Failed to load tenants:', err?.message ?? err);
        return [] as UserTenant[];
      }
    },
    enabled: !!routeTenantId && !!user,
  });
  const tenants: UserTenant[] = tenantsData ?? [];

  const handleSwitchTenant = async (tenantId: string) => {
    // Bajo el dominio propio cada empresa vive en su subdominio (<código>.agrovid.solutions) y la
    // sesión no se comparte entre subdominios: se salta a la otra empresa y se entra allí.
    const targetCode = tenants.find((x) => x.tenantId === tenantId)?.code?.replace(/^\//, '');
    const hostUrl = targetCode ? tenantHostUrl(targetCode, '/') : null;
    if (hostUrl) {
      setShowTenantDropdown(false);
      window.location.assign(hostUrl);
      return;
    }
    try {
      const result = await impersonateTenant(tenantId);
      const code = (result.tenant.code ?? tenantId).replace(/^\//, '');
      impersonate(result.token, {
        ...user!,
        tenantId: result.tenant.tenantId,
        tenantCode: code,
      });
      setTenant(code, result.tenant.name, result.tenant.logoUrl ?? undefined);
      setShowTenantDropdown(false);
      // Navegación completa en vez de navigate(): al actualizar la sesión, AppShell
      // veía la URL de la empresa anterior contra el tenantCode nuevo, lo tomaba
      // por una sesión de otra empresa y mandaba a /login antes de llegar al
      // nuevo destino. La recarga además descarta la caché de React Query de la
      // empresa anterior. La sesión ya está en sessionStorage.
      window.location.assign(`/${code}/dashboard`);
    } catch (err) {
      // Cambio fallido: no se toca la sesión ni se navega; solo se informa el motivo.
      console.error('Failed to switch tenant:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>{t.superAdmin.loginError}</ToastTitle>
          <ToastBody>{apiErrorMessage(err, t.superAdmin.loginErrorBody)}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  };

  const queryClient = useQueryClient();
  const handleLogout = () => {
    performLogout({
      logout,
      clearCache: () => queryClient.clear(),
      navigate: (to) => navigate(to),
      tenantId: currentTenantId,
    });
  };

  const ranchoName = tenantDisplayName ?? (user as { ranchoName?: string } | null)?.ranchoName ?? 'Agrovid';
  const displayName = user?.name ?? user?.phoneNumber ?? '?';
  const roleLabels = t.topBar.roleLabels as Record<string, string>;
  const activeRoleLabel = user?.activeRole
    ? (roleLabels[user.activeRole] ?? user.activeRole)
    : (user?.roles?.[0] ? (roleLabels[user.roles[0]] ?? user.roles[0]) : '');

  return (
    <header className={s.bar}>
      {/* ── Left ── */}
      <div className={s.left}>
        <Button
          className={s.menuBtn}
          appearance="subtle"
          size="small"
          icon={<Navigation24Regular />}
          onClick={onMenuClick}
          aria-label={t.nav.openMenu}
        />

        {/* Tenant selector with dropdown */}
        {tenants.length > 1 && (
          <Popover open={showTenantDropdown} onOpenChange={(_, data) => setShowTenantDropdown(data.open)}>
            <PopoverTrigger disableButtonEnhancement>
              <div className={s.tenantSelector} role="button" tabIndex={0} aria-label={t.topBar.selectTenant}>
                {tenantLogoUrl ? (
                  <img
                    src={tenantLogoUrl}
                    alt={ranchoName}
                    style={{ height: '28px', width: 'auto', borderRadius: '3px', objectFit: 'contain' }}
                  />
                ) : (
                  <div
                    style={{
                      height: '28px', width: '28px', borderRadius: '3px',
                      backgroundColor: avatarColor(ranchoName),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0,
                    }}
                  >
                    {getInitials(ranchoName, ranchoName)}
                  </div>
                )}
                <span className={s.tenantSelectorLabel}>{ranchoName}</span>
                <ChevronDown24Regular fontSize={16} />
              </div>
            </PopoverTrigger>
            <PopoverSurface>
              <div className={s.tenantDropdown}>
                {loadingTenants ? (
                  <div className={s.tenantDropdownLoading}>
                    <Spinner size="small" />
                    <span>{t.common.loading}</span>
                  </div>
                ) : (
                  tenants.map(tenant => (
                    <div
                      key={tenant.tenantId}
                      className={`${s.tenantDropdownItem} ${
                        isCurrentTenant(tenant.tenantId, user?.tenantId, currentTenantId) ? s.tenantDropdownItemSelected : ''
                      }`}
                      onClick={() => handleSwitchTenant(tenant.tenantId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSwitchTenant(tenant.tenantId);
                        }
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                    >
                      {/* Avatar with initials */}
                      <div
                        style={{
                          width: '32px', height: '32px', minWidth: '32px',
                          borderRadius: '4px',
                          backgroundColor: avatarColor(tenant.name),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0,
                        }}
                      >
                        {getInitials(tenant.name, tenant.name)}
                      </div>
                      {/* Name and code in separate lines */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 500, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tenant.name}
                        </span>
                        {tenant.code && (
                          <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tenant.code}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </PopoverSurface>
          </Popover>
        )}

        {/* Static display if only one tenant */}
        {tenants.length <= 1 && (
          <>
            {tenantLogoUrl ? (
              <img
                src={tenantLogoUrl}
                alt={ranchoName}
                style={{ height: '32px', width: 'auto', borderRadius: '4px', objectFit: 'contain' }}
              />
            ) : (
              <div
                style={{
                  height: '32px', width: '32px', borderRadius: '4px',
                  backgroundColor: avatarColor(ranchoName),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0,
                }}
              >
                {getInitials(ranchoName, ranchoName)}
              </div>
            )}
            <Text size={400} weight="semibold" className={s.ranchoName}>
              {ranchoName}
            </Text>
          </>
        )}

        {/* F1 — Proyecto/temporada activo. Se oculta si el usuario no puede
            leer proyectos o si el tenant todavía no tiene ninguno. */}
        {canReadProjects && projects.length > 0 && (
          <Dropdown
            size="small"
            aria-label={t.topbar.project}
            placeholder={t.topbar.allProjects}
            value={projects.find(p => p.projectId === activeProjectId)?.name ?? t.topbar.allProjects}
            selectedOptions={[activeProjectId ?? '']}
            onOptionSelect={(_, data) => {
              const id = data.optionValue || null;
              setActiveProject(id, projects.find(p => p.projectId === id)?.name ?? null);
            }}
            style={{ minWidth: '180px', maxWidth: '240px' }}
          >
            <Option value="">{t.topbar.allProjects}</Option>
            {projects.map(p => (
              <Option key={p.projectId} value={p.projectId}>{p.name}</Option>
            ))}
          </Dropdown>
        )}

        {/* Fase 1 rancho selector (2026-09-27) — solo aparece con acceso a
            2+ ranchos; con uno solo (el caso más común) sobra el selector. */}
        {canReadRanchos && visibleRanchos.length > 1 && (
          <Dropdown
            size="small"
            aria-label={t.topbar.rancho}
            placeholder={t.topbar.allRanchos}
            value={visibleRanchos.find(r => r.ranchoId === activeRanchoId)?.name ?? t.topbar.allRanchos}
            selectedOptions={[activeRanchoId ?? '']}
            onOptionSelect={(_, data) => {
              const id = data.optionValue || null;
              setActiveRancho(id, visibleRanchos.find(r => r.ranchoId === id)?.name ?? null);
            }}
            style={{ minWidth: '180px', maxWidth: '240px' }}
          >
            <Option value="">{t.topbar.allRanchos}</Option>
            {visibleRanchos.map(r => (
              <Option key={r.ranchoId} value={r.ranchoId}>{r.name}</Option>
            ))}
          </Dropdown>
        )}
      </div>

      {/* ── Right ── */}
      <div className={s.right}>

        {/* Idioma — visible para todos los roles, incl. INVESTOR (read-only) */}
        <LanguageSwitcher />

        {/* Version badge (Fase 2) — mostrar versión del backend */}
        {backendVersion && (
          <div className={s.versionBadge} title={`Backend v${backendVersion}`}>
            v{backendVersion}
          </div>
        )}

        {/* Dark / Light toggle */}
        <Button
          icon={mode === 'dark' ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />}
          appearance="subtle"
          size="small"
          onClick={toggle}
          aria-label={t.nav.changeTheme}
          title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        />

        {/* Guía del Sistema / Ayuda */}
        <Button
          icon={<QuestionCircle24Regular />}
          appearance="subtle"
          size="small"
          onClick={() => setGuideOpen(true)}
          aria-label={t.systemGuide?.helpTooltip ?? 'Guía del Sistema / Ayuda'}
          title={t.systemGuide?.helpTooltip ?? 'Guía del Sistema / Ayuda'}
        />
        <SystemGuideModal open={guideOpen} onOpenChange={setGuideOpen} onStartTour={startTour} />

        {/* Notification Bell */}
        <Popover>
          <PopoverTrigger disableButtonEnhancement>
            <div className={s.bellWrap}>
              <Button
                icon={unreadCount > 0 ? <AlertBadge24Regular /> : <Alert24Regular />}
                appearance="subtle"
                size="small"
                aria-label={t.notifications.bell}
                onClick={markAllRead}
              />
              {unreadCount > 0 && (
                <Badge
                  className={s.bellBadge}
                  size="small"
                  color="danger"
                  appearance="filled"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </div>
          </PopoverTrigger>

          <PopoverSurface>
            <div className={s.panel}>
              <div className={s.panelHeader}>
                <Text weight="semibold" size={300}>{t.notifications.panelTitle}</Text>
                {notifications.length > 0 && (
                  <Button size="small" appearance="subtle" onClick={markAllRead}>
                    {t.notifications.markAllRead}
                  </Button>
                )}
              </div>
              <div className={s.panelList}>
                {notifications.length === 0 ? (
                  <div className={s.panelEmpty}>{t.notifications.empty}</div>
                ) : (
                  notifications.slice(0, 20).map(n => {
                    const isUnread = n.occurredAt > lastSeenAt;
                    return (
                      <div
                        key={n.id}
                        className={`${s.notifItem} ${isUnread ? s.notifItemUnread : ''}`}
                      >
                        <span className={s.notifTitle}>{n.title}</span>
                        {n.body && <span className={s.notifBody}>{n.body}</span>}
                        <span className={s.notifTime}>{formatRelativeTime(n.occurredAt, t)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </PopoverSurface>
        </Popover>

        {/* ── Avatar + Profile Popover ─────────────────────────────────── */}
        <Popover positioning="below-end">
          <PopoverTrigger disableButtonEnhancement>
            <button className={s.avatarBtn} aria-label={t.nav.viewProfile}>
              <AvatarSmall name={user?.name} photoUrl={user?.photoUrl} />
            </button>
          </PopoverTrigger>

          <PopoverSurface>
            <div className={s.profilePanel}>

              {/* Header: avatar grande + nombre + rol */}
              <div className={s.profileHeader}>
                <AvatarLarge name={user?.name} photoUrl={user?.photoUrl} />
                <div className={s.profileInfo}>
                  <span className={s.profileName}>{displayName}</span>
                  {activeRoleLabel && (
                    <span className={s.profileRole}>{activeRoleLabel}</span>
                  )}
                </div>
              </div>

              <Divider />

              {/* Campos de contacto */}
              <div className={s.profileFields}>

                {/* Teléfono */}
                {user?.phoneNumber && (
                  <div className={s.profileRow}>
                    <span className={s.profileRowIcon}>
                      <Phone24Regular fontSize={16} />
                    </span>
                    <span>{user.phoneNumber}</span>
                  </div>
                )}

                {/* Email */}
                {user?.email && (
                  <div className={s.profileRow}>
                    <span className={s.profileRowIcon}>
                      <Mail24Regular fontSize={16} />
                    </span>
                    <span>{user.email}</span>
                  </div>
                )}

                {/* Rancho(s) / Lote(s) asignados */}
                <div className={s.profileRow} data-testid="assigned-access">
                  <span>
                    {t.topBar.assignedRanchos}: {assignedRanchoNames.length ? assignedRanchoNames.join(', ') : t.topBar.assignedAll}
                    {' · '}
                    {t.topBar.assignedLotes}: {assignedLoteNames.length ? assignedLoteNames.join(', ') : t.topBar.assignedAll}
                  </span>
                </div>

                {/* Telegram: estado, vinculación y desvinculación */}
                <div className={s.profileRow} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <TelegramLinkSection initialLinked={!!user?.telegramId} />
                </div>

                {/* Cierre de día proactivo — solo tiene sentido con Telegram vinculado */}
                {user?.telegramId && (
                  <div className={s.profileRow} style={{ flexDirection: 'column', alignItems: 'stretch', gap: tokens.spacingVerticalXS }}>
                    <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                      {t.topBar.dayCloseTimeLabel}
                    </span>
                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' }}>
                      <input
                        type="time"
                        value={dayCloseTime}
                        onChange={(e) => handleDayCloseTimeChange(e.target.value)}
                        aria-label={t.topBar.dayCloseTimeLabel}
                        style={{ flex: 1 }}
                      />
                      {dayCloseTime && (
                        <Button size="small" appearance="subtle" onClick={handleDayCloseTimeDisable}>
                          {t.topBar.dayCloseTimeDisable}
                        </Button>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: tokens.colorNeutralForeground4 }}>
                      {t.topBar.dayCloseTimeHint}
                    </span>
                  </div>
                )}

              </div>

              {/* Footer: Preferencias — reactivar tour + Cerrar sesión */}
              <div className={s.profileFooter}>
                <Button
                  icon={<QuestionCircle20Regular />}
                  appearance="subtle"
                  size="small"
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                  onClick={() => startTour()}
                >
                  {t.topBar.restartTourLabel}
                </Button>
                <Button
                  icon={<SignOut24Regular />}
                  appearance="subtle"
                  size="small"
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                  onClick={handleLogout}
                >
                  {t.auth.logOut}
                </Button>
              </div>

            </div>
          </PopoverSurface>
        </Popover>

      </div>
    </header>
  );
}
